import { compact } from '@lglp/utils';
import { compose, defaultTo, has, map, path, prop, propOr } from 'ramda';

import { date as dateParser } from '../../../../helpers/compact';
import { DocumentType } from '../../../../types/document';
import { toLinkFromProp } from '../../fields/link';
import { processFinancialFooter } from './financial/financial-footer';
import { deprecatedPageEntity } from './internal';

/**
 * @typedef  {Object} PrismicFinancialDebt
 */

/**
 * @typedef {DocumentSchema} FinancialDebtSchema
 */
const medias = compose(compact, map(toLinkFromProp('fichier')), defaultTo([]));

const date = (propname, data) =>
  has(propname, data) &&
  dateParser(prop(propname, data), 'YYYY-MM-DDTHH:mm:ssZ');

const emtn = (prefix, data) =>
  compact({
    transmitter: prop(`${prefix}_programme_emtn_emetteur`, data),
    roof: prop(`${prefix}_programme_emtn_plafond`, data),
    currency: prop(`${prefix}_programme_emtn_devises`, data),
    agents: prop(`${prefix}_programme_emtn_agents_placeurs`, data),
    medias: medias(prop(`${prefix}_programme_emtn_medias`, data)),
  });

const bond = (data) =>
  compact({
    transmissionDate: date('date_emission', data),
    termDate: date('date_echeance', data),
    termText: prop('autre_echeance', data),
    amount: prop('montant', data),
    coupon: prop('coupon', data),
    place: prop('place_cotation', data),
    isin: prop('code_isin', data),
    doc: toLinkFromProp('documentation', data),
  });

const bonds = (data) => compact(map(bond, data));

const greenBonds = (prefix, data) => {
  const d = path([`${prefix}_green_bonds_bond`, '0'], data);

  return compact({
    bond: d && bond(d),
    medias: medias(prop(`${prefix}_green_bonds_medias`, data)),
  });
};

const processLongTab = (prefix, data) =>
  compact({
    emtn: emtn(prefix, data),
    issuedBonds: bonds(propOr([], `${prefix}_obligations_emises`, data)),
    otherBonds: bonds(propOr([], `${prefix}_titres_subordonnes_emis`, data)),
    banking: prop(`${prefix}_lignes_bancaires`, data),
    greenBonds: greenBonds(prefix, data),
  });

const marketableSecurities = (prefix, data) =>
  compact({
    transmitter: prop(`${prefix}_emetteur`, data),
    roof: prop(`${prefix}_plafond`, data),
    currency: prop(`${prefix}_devises`, data),
    rating: prop(`${prefix}_rating`, data),
    agents: prop(`${prefix}_agents_placeurs`, data),
    others: prop(`${prefix}_others`, data),
    medias: medias(prop(`${prefix}_medias`, data)),
  });

const comPaper = (prefix, data) =>
  compact({
    transmitter: prop(`${prefix}_emetteur`, data),
    roof: prop(`${prefix}_plafond`, data),
    currency: prop(`${prefix}_devises`, data),
    agents: prop(`${prefix}_agents_placeurs`, data),
    medias: medias(prop(`${prefix}_medias`, data)),
  });

const processShortTab = (prefix, data) =>
  compact({
    marketableSecurities: marketableSecurities(`${prefix}_titre_nego`, data),
    comPaper: comPaper(`${prefix}_com_paper`, data),
  });

const processHeader = (data) =>
  compact({
    grossDebt: prop('grossDebt', data),
    netDebt: prop('netDebt', data),
    ratingSP: prop('ratingSP', data),
    ratingFitch: prop('ratingFitch', data),
  });

/**
 * Transform the given input into objects
 *
 * @param  {PrismicFinancialDebt} data
 * @return {FinancialDebtSchema}
 */
const processFinancialDebt = (data) =>
  compact({
    uid: DocumentType.FINANCIAL_DEBT,
    type: DocumentType.FINANCIAL_DEBT,
    ...processFinancialFooter(data),
    ...processHeader(data),
    shortTerm: processShortTab('short', data),
    longTerm: processLongTab('long', data),
  });

/**
 * Normalizr schema representing a financial debt page
 */
export const FinancialDebtEntity = deprecatedPageEntity(
  DocumentType.FINANCIAL_DEBT,
  {
    processStrategy: (entity) => ({
      ...processFinancialDebt(prop('data', entity)),
    }),
  }
);
